import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ConfirmDialogService, ScreenSizeDetectorService, UiKitModule } from '@serviceplan/ui-kit';
import {
  AssetUIModel,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  accessAsset,
  accessGlobal,
  anyAsset,
} from '@thehubz/client/core';
import { Asset, AssetDetails, AssetsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { AssetCommonService } from '../../../services/asset-common.service';
import { CollectionStepperDialogService } from '../../../services/collection-stepper-dialog.service';
import { SharingStepperDialogService } from '../../../services/sharing-stepper-dialog.service';
import { AssetsPageService } from '../../assets/assets-page/assets-page.service';

@Component({
  selector: 'thehubz-asset-action-menu',
  templateUrl: './asset-action-menu.component.html',
  styleUrls: ['./asset-action-menu.component.scss'],
  standalone: true,
  imports: [CommonModule, MatIconModule, UiKitModule, MatDividerModule, ClientCoreModule, MatMenuModule],
})
export class AssetActionMenuComponent {
  @Input() asset!: AssetUIModel;
  @Input() assetIndex!: number;
  @Input() configObject!: AnyType;
  @Input() collectionOwner = false;
  @Input() collectionFlag?: boolean = false;
  @Output() deleteAsset = new EventEmitter<AnyType>();
  @Output() clickBasket = new EventEmitter<AnyType>();
  @Output() downloadAsset = new EventEmitter<AnyType>();
  @Output() exportAsset = new EventEmitter<AnyType>();
  @Output() editAsset = new EventEmitter<AnyType>();
  @Output() editDraftAsset = new EventEmitter<AnyType>();

  AccessAsset = accessAsset;
  AccessGlobal = accessGlobal;
  AnyAsset = anyAsset;
  spinnerDiameter = 50;
  isAddedToBasket = false;
  assetAspectRatio: AssetDetails[] = [];
  brandingKey!: CustomClients;
  CustomClients = CustomClients;
  Status = Asset.StatusEnum;

  textAddToBasket = $localize`Add to basket`;
  textRemoveFromBasket = $localize`Remove from basket`;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private assetService: AssetsApiService,
    private confirmDialog: ConfirmDialogService,
    private assetsPageService: AssetsPageService,
    protected assetCommonService: AssetCommonService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
    private sharingStepperDialogService: SharingStepperDialogService,
    protected collectionStepperDialogService: CollectionStepperDialogService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  async shareAsset() {
    const assetID = this.asset._id;
    const aspectRatioIDs = this.assetAspectRatio.map((asset: AssetDetails) => asset.assetId);
    const assetIDx = aspectRatioIDs.findIndex((id) => id === assetID);
    if (assetIDx === -1) {
      const stepperData = await this.sharingStepperDialogService.createStepperModel(
        this.assetAspectRatio,
        $localize`Share assets` + `:&nbsp;<span class="font-light">${this.asset.name}<span>`,
      );
      this.sharingStepperDialogService.openSharingDialog(stepperData);
    }
  }

  editAssets(editType: string) {
    const selectedAssets = this.asset;
    const assetObject = {
      editType: editType,
      assets: [selectedAssets],
    };
    this.assetsPageService.editDialogConfirmation(assetObject.assets).subscribe((data: AnyType) => {
      if (data) {
        this.editAsset.emit(assetObject);
      }
    });
  }

  updateDraftAsset() {
    const selectedAssets = this.asset;
    const assetObject = {
      assets: [selectedAssets],
    };
    this.editDraftAsset.emit(assetObject);
  }

  private async getAssetDetail(assetID: string) {
    return new Promise<AnyType>((resolve, reject) => {
      this.assetService.assetsControllerFindOne(assetID).subscribe({
        next: async (asset) => {
          if (asset) {
            await this.setAssetDetails(asset);
            resolve(asset);
          }
        },
        error: (err) => {
          this.clientNotificationHandlerService.errorNotification(err);
          reject(err);
        },
      });
    });
  }

  async setAssetDetails(assetObj: Asset) {
    if (assetObj.currentVersion?.aspectRatios) {
      this.assetAspectRatio.push({
        assetId: assetObj._id,
        name: assetObj.name,
        version: assetObj.currentVersion?.version || 0,
        aspectRatios: assetObj.currentVersion?.aspectRatios || [],
      });
    }
  }

  addToCollection() {
    this.collectionStepperDialogService.openDialogForCollection([this.asset]);
  }

  removeFromCollection(assetID: string, idx: string) {
    this.confirmDialog
      .open({
        title: $localize`Remove asset`,
        message: $localize`Are you sure you want to remove this asset from the collection?`,
      })
      .subscribe((confirm: boolean) => {
        if (confirm) {
          this.assetCommonService.collectionRemoveAsset$.next([{ assetID: assetID }]);
        }
      });
  }
}
